import { CANCEL_CODE, type IErrorData } from '@/utils/getErrorData';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getOrdersThunk } from './ordersThunks';
import { type IOrderListItem } from '@/types/IOrder';
import { type TOrdersStatus } from '@/types/IOrdersStatus';
import { byNewestFirst } from '@/utils/mergeLiveOrders';

export interface IOrdersState {
  /** Выдача `GET /api/admin/v2/orders` — элементы без позиций (PAR-5189). */
  orders: IOrderListItem[];
  total: number;
  status: TOrdersStatus;
  error: IErrorData | null;
  /** Заказы из сокета — та же форма, что у элемента выдачи v2. */
  liveOrders: IOrderListItem[];
}

const initialState: IOrdersState = {
  orders: [],
  total: 0,
  status: 'idle',
  error: null,
  liveOrders: []
};

const orders = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    resetOrders(state) {
      state.orders = initialState.orders;
      state.total = initialState.total;
      state.status = initialState.status;
      state.error = initialState.error;
      state.liveOrders = initialState.liveOrders;
    },
    /**
     * Upsert по `uuid`: повторное событие по тому же заказу заменяет запись.
     * До PAR-5189 ключом был `id`, которого в событии больше нет, — и все
     * заказы без `id` схлопнулись бы в одну запись.
     */
    addLiveOrder(state, action: PayloadAction<IOrderListItem>) {
      const incoming = action.payload;
      const existingIndex = state.liveOrders.findIndex(
        (order) => order.uuid === incoming.uuid
      );

      if (existingIndex !== -1) {
        state.liveOrders[existingIndex] = incoming;
        return;
      }

      // Держим эфемерный слой в порядке «новые сверху» (как серверная выдача),
      // чтобы заказы, пришедшие не по порядку, вставали на свои места.
      const insertAt = state.liveOrders.findIndex(
        (order) => byNewestFirst(order, incoming) > 0
      );

      if (insertAt === -1) {
        state.liveOrders.push(incoming);
      } else {
        state.liveOrders.splice(insertAt, 0, incoming);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrdersThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        // Любой запрос списка возвращает нас к «правде бэка» —
        // эфемерный realtime-слой при этом сбрасывается.
        state.liveOrders = [];
      })
      .addCase(getOrdersThunk.fulfilled, (state, action) => {
        state.orders = action.payload.items;
        state.total = action.payload.total;
        state.status = 'success';
        state.error = null;
      })
      .addCase(getOrdersThunk.rejected, (state, action) => {
        if (action.payload?.code === CANCEL_CODE) {
          return;
        }

        state.status = 'error';
        state.error = action.payload ?? {
          message: 'Ошибка при получении заказов'
        };
      });
  }
});

export const { resetOrders, addLiveOrder } = orders.actions;
export default orders.reducer;
